"use client";

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useAppSelector } from "@/store/hooks";
import type { MenuItem } from "@/store/slices/menuSlice";

const Menu = () => {
  const searchParams = useSearchParams();
  const { data: menuData } = useAppSelector((state) => state.menu);

  // Derived categories from server data
  const categories = useMemo(() => {
    if (!menuData?.sections) return ["Lunch", "Dinner", "Dessert", "Drinks"];
    return menuData.sections.map((s) => s.title);
  }, [menuData]);

  const [activeTab, setActiveTab] = useState(categories[0] || "Lunch");

  const currentItems = useMemo(() => {
    if (!menuData?.sections) return [];
    const section = menuData.sections.find((s) => s.title === activeTab);
    return section?.items || [];
  }, [menuData, activeTab]);

  // Split items into two columns
  const firstCol = currentItems.slice(0, Math.ceil(currentItems.length / 2));
  const secondCol = currentItems.slice(Math.ceil(currentItems.length / 2));

  return (
    <section id="menu" className="py-24 bg-white text-center">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Section Header */}
        <div className="mb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="font-sacramento text-4xl text-[#c59d5f] mb-4"
          >
            Our Favourites
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="font-cormorant text-4xl md:text-[50px] text-[#1a1a1a] mb-8 font-bold leading-tight tracking-tight"
          >
            Discover Our Menu
          </motion.h2>

          {/* Decorative Divider */}
          <div className="flex justify-center items-center gap-4 mb-10">
            <div className="w-12 h-[1px] bg-[#c59d5f]/30"></div>
            <div className="flex gap-1">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rotate-45 border border-[#c59d5f]"
                ></div>
              ))}
            </div>
            <div className="w-12 h-[1px] bg-[#c59d5f]/30"></div>
          </div>
        </div>

        {/* Categories Tabs */}
        <div className="flex justify-center flex-wrap gap-8 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`relative font-inter text-[14px] font-bold tracking-widest transition-colors duration-300 pb-2 ${
                activeTab === cat
                  ? "text-[#c59d5f]"
                  : "text-[#1a1a1a] hover:text-[#c59d5f]"
              }`}
            >
              {cat}
              {activeTab === cat && (
                <motion.div
                  layoutId="activeTarget"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#c59d5f]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Menu Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 md:px-8"
          >
            {[firstCol, secondCol].map((column, colIdx) => (
              <div
                key={colIdx}
                className="relative border border-[#c59d5f]/60 rounded-[2px] h-full"
              >
                <div className="absolute -top-[4px] -bottom-[4px] left-[2px] right-[2px] border border-[#c59d5f]/60 rounded-[2px] pointer-events-none"></div>

                <div className="p-6 md:p-8 text-left space-y-6 h-full">
                  {column.map((item, index) => (
                    <div key={item.id || index} className="group/item">
                      <div className="flex flex-col">
                        {/* Price only - button removed as per request */}
                        <div className="flex justify-between items-start gap-4 mb-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-playfair text-[18px] md:text-[20px] text-[#1a1a1a] font-semibold transition-colors group-hover/item:text-[#c59d5f] tracking-tight">
                              {item.title}
                            </h3>
                          </div>
                          <span className="font-playfair text-lg md:text-xl text-[#c59d5f] font-semibold whitespace-nowrap">
                            £{item.price}
                          </span>
                        </div>
                        <p className="text-[12px] md:text-[15px] text-[#9b9b9b] font-playfair leading-relaxed italic">
                          {item.description}
                        </p>
                      </div>
                      {/* Dashed Separator */}
                      {index !== column.length - 1 && (
                        <div className="w-full border-t border-dashed border-[#dbdbdb] mt-6"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Footer Link */}
        <div className="mt-8">
          {(() => {
            const mode = searchParams.get("mode");
            const slug = searchParams.get("slug");
            const query = mode && slug ? `?mode=${mode}&slug=${slug}` : "";
            return (
              <Link
                href={`/menu${query}`}
                className="inline-block font-playfair text-[16px] font-bold text-[#1a1a1a] border-b-2 border-[#212529] pb-1 hover:text-[#c59d5f] hover:border-[#c59d5f] transition-all duration-300"
              >
                Explore Full Menu
              </Link>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default Menu;
